<template>
	<div class="sc-table-container">
		<table class="disbursements-table" v-if="store.disbursements.length > 0">
			<thead>
				<tr>
					<th
						v-for="header in tableHeaders"
						:key="header.name"
						:style="{ minWidth: header.minWidth }">
						{{ header.label }}						
					</th>
				</tr>
			</thead>
			<tbody>
				<disbursements-table-row
					v-for="(disbursement, index) in store.disbursements"
					:key="disbursement.form?.id || `new-${index}`"
					ref="rowRefs"
					:disbursements="store.disbursements"
					:disbursement="disbursement"
					:certification="certification"
					:index="index"
					@toggleEdit="$emit('toggleEdit', $event)"
					@hasDirtyChanged="onRowDirtyChanged"
					@postDisbursementChangeReady="onPostDisbursementChangeReady"
				>
				</disbursements-table-row>
			</tbody>
		</table>

		<div v-else class="empty-state">
			<p>No disbursements found for this certification.</p>
		</div>

		<div class="add-new-container">
			<button 
				class="add-new-link" 
				@click="handleAddNew"
				:disabled="isAddDisabled"
				v-if="canAddNew"
			>
				+ Add New Disbursement
			</button>
			<span v-if="hasReachedLimit" class="limit-message">
				Maximum of 4 disbursements reached
			</span>
		</div>

		<DisbursementConfirmModal
			v-if="showConfirmModal"
			:changes="changesSummary"
			:isSaving="isSaving"
			:canGenerateModificationFile="props.certification.canGenerateModificationFile"
			@confirm="handleConfirmSave"
			@cancel="handleCancelConfirm"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch, nextTick } from "vue";
import { Patch, PatchVM } from "@/types/json-patch";
import DisbursementsTableRow from "@/components/v2/disbursements/DisbursementsTableRow.vue";
import {
	DisbursementVM,
	DisbursementFormVM,
	ToggleEvent,
	DisbursementAddedEvent,
	DisbursementBatchSaveRequest,
	DisbursementPatchRequest,
	CreatePostDisbursementChangeRequest,
	AddDisbursementRequest,
	DisbursementChangesSummary
} from "@/types/disbursements";
import DisbursementConfirmModal from '@/components/v2/disbursements/DisbursementConfirmModal.vue';
import { CertificationVM } from "@/types/certifications";
import { $api } from '@/services/api1';
import functions from "@/use/functions.js";
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';

const props = defineProps<{
	data: DisbursementVM[];
	certification: CertificationVM;  
	isLoading : boolean;
	editingPostDisbFromHistory?: {
		disbursementId: number;
		postDisbursementChangeAmountLedgerId: number;
	} | null;
}>();

const store = useDisbursementsStore();

const emit = defineEmits<{
	(
		e: "disbursementUpdated",
		value: { disbursement: DisbursementVM; disbursementIndex: number }
	): void;
	(e: "disbursementAdded", value: DisbursementAddedEvent): void;
	(e: "disbursementRowDeleted", value: number): void;
	(e: "toggleEdit", value: ToggleEvent): void;
	(e: "hasDirtyChanged", value: { hasDirty: boolean }): void;
	(e: "batchSaveComplete", value: DisbursementVM[]): void;
	(e: "saveCancelled"): void;
	(e: "saveValidationFailed"): void;
	(e: "saveFailed"): void;
	(e: 'clearEditRequest'): void;
}>();

const totalDisbursements = computed(() => store.disbursements.length);
const hasReachedLimit = computed(() => totalDisbursements.value >= 4);
const canAddNew = computed(() => !props.isLoading && !hasReachedLimit.value);
const isAddDisabled = computed(() => props.isLoading || hasReachedLimit.value || hasDirty.value);
const isSaving = ref(false);
const justSaved = ref(false);
const hasDirty = computed(() => store.hasDirty);

type RowInstance = InstanceType<typeof DisbursementsTableRow> & {
	saveIfDirty?: () => Promise<void>;
	resetChanges?: () => void;
	getDisbursementId?: () => number;
	getSequenceNumber?: () => string;
	getPatches?: () => Promise<{ patches: Patch[]; patchesVM: PatchVM[] } | null>;s
	validateForm?: () => Promise<boolean>;
	getPendingPostDisbursementChange?: () => Promise<CreatePostDisbursementChangeRequest | null>;
	getFormData?: () => DisbursementFormVM;
};

const rowRefs = ref<RowInstance[]>([]);

onBeforeUnmount(() => {
	rowRefs.value = [];
});

const showConfirmModal = ref(false);
const changesSummary = ref<DisbursementChangesSummary[]>([]);

function onRowDirtyChanged({ index, dirty }: { index: number; dirty: boolean }) {	
	if (props.isLoading) {
		return;
	}

	const disbursement = store.disbursements[index];
	if (!disbursement) return;
	
	const disbId = disbursement.details.id;
	
	if (dirty) {
		store.markDirty(disbId);
	} else {
		store.clearDirty(disbId);
	}
	
	emit("hasDirtyChanged", { hasDirty: store.hasDirty });
}

function onPostDisbursementChangeReady() {  
	emit("hasDirtyChanged", { hasDirty: true });
}

async function saveAll() {
	   
	// Step 1: Validate all rows
	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (row?.validateForm) {
			const isValid = await row.validateForm();
			if (!isValid) {
				emit('saveValidationFailed');         
				functions.openModal({
					title: 'Validation Error',
					description: `Please fix validation errors before saving.`,
				});
				return;
			}
		}
	}
	
	// Step 2: Collect changes for confirmation
	changesSummary.value = await collectChangesForConfirmation();
	
	if (changesSummary.value.length === 0) {      
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });
		return;
	}

	// Step 3: Validate with backend API
	const batchRequest = await buildBatchRequest(false);
	
	try {
		const validationResponse = await $api.disbursements.ValidateDisbursementBatch(batchRequest);
		
		if (!validationResponse.success || !validationResponse.results) {				
			emit('saveValidationFailed');

			const errorMessage = validationResponse.errors?.length > 0
				? validationResponse.errors.join('<br/>')
				: 'Total disbursement amount exceeds certified amount';
			
			functions.openModal({
				title: 'Validation Error',
				html: errorMessage,
			});
			return;
		}
	} catch (error) {			
		emit('saveValidationFailed')
		functions.openModal({
			title: 'Validation Error',
			description: 'Failed to validate disbursements. Please try again.',
		});
		return;
	}
	
	// Step 4: Show confirmation modal
	showConfirmModal.value = true;
}

async function handleConfirmSave(generateModFile: boolean) {  
	isSaving.value = true;
	try {		
		await performBatchSave(generateModFile);
	} catch (error) {
		showConfirmModal.value = false;
		isSaving.value = false;
		emit('saveFailed');
		functions.openModal({
			title: 'Error',
			description: 'Failed to save changes. Please try again.',
		});
	}
}

function handleCancelConfirm() {
	showConfirmModal.value = false;
	isSaving.value = false;
	changesSummary.value = [];
	emit('saveCancelled');
}

async function buildBatchRequest(generateModFile: boolean): Promise<DisbursementBatchSaveRequest> {
	const updateDisbursements: DisbursementPatchRequest[] = [];
	const createDisbursementRequests: AddDisbursementRequest[] = [];

	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (!row || !disbursement) continue;

		const disbursementId = row.getDisbursementId?.() || 0;
		const sequenceNumber = row.getSequenceNumber?.() || '';

		if (disbursement.details.isNew) {
			const formData = row.getFormData?.();
			
			if (formData) {
				const addRequest: AddDisbursementRequest = {
					disbursement: formData,
					certifiedAmount: props.certification.form.certifiedAmount || props.certification.initialFormValues.certifiedAmount,
					certificationSequenceNumber: props.certification.form.sequenceNumber,
					certificationId: props.certification.id,
					loanId: props.certification.loanId,
					appId: props.certification.appId,
					cluid: props.certification.initialFormValues.cluid,
					lenderId: props.certification.header.lenderId,
					productTypeId: props.certification.productTypeId,
					referenceId: props.certification.referenceId,
					borrowerFirstName: props.certification.borrowers.borrower.firstName,
					borrowerLastName: props.certification.borrowers.borrower.lastName,
					disbursementDateValidated: true,
					formOptions: props.certification.disbursementFormOptions,
				};
				
				createDisbursementRequests.push(addRequest);
			}
		} else {			
			const patchData = await row.getPatches?.();
			const postDisbChange = await row.getPendingPostDisbursementChange?.();
						
			if (patchData?.patches || postDisbChange) {				
				const updateRequest: DisbursementPatchRequest = {
					patchDocument: patchData?.patches || [],
					patchesVM: patchData?.patchesVM || [],
					applicationId: props.certification.appId,
					loanId: props.certification.loanId,
					certificationId: props.certification.id,
					disbursementId: disbursementId,
					certificationSequenceNumber: props.certification.initialFormValues.sequenceNumber,
					disbursementSequenceNumber: sequenceNumber,
				};
								
				if (postDisbChange?.data) {					
					updateRequest.postDisbursementChange = postDisbChange.data;
				} else if (postDisbChange?.patches) {					
					updateRequest.postDisbursementChangePatch = postDisbChange.patches;
				}
				
				updateDisbursements.push(updateRequest);
			}
		}
	}
	
	return {
		certificationId: props.certification.id,
		applicationId: props.certification.appId,
		loanId: props.certification.loanId,
		updateDisbursements: updateDisbursements,
		addDisbursements: createDisbursementRequests.length > 0 ? createDisbursementRequests : undefined,
		generateModificationFile: generateModFile,
	};
}

async function performBatchSave(generateModFile: boolean) {	
	const request = await buildBatchRequest(generateModFile);  
		
	if (request.updateDisbursements.length === 0 &&
		(!request.addDisbursements || request.addDisbursements.length === 0)) {
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });
		return;
	}

	const response = await $api.disbursements.SaveDisbursementBatch(request);
		
	if (response.success) {
		justSaved.value = true;
		store.setJustSaved(true);

		emit("batchSaveComplete", response.results);
		await nextTick();		
		for (const row of rowRefs.value) {
			row?.resetChanges?.();
		}
						
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });

		showConfirmModal.value = false;
		isSaving.value = false;

		setTimeout(() => {
			justSaved.value = false;
		}, 1000);
		
		functions.openModal({
			title: 'Success',
			description: 'All changes have been saved successfully.',
		});
	} else {
		showConfirmModal.value = false;
		isSaving.value = false;
		emit('saveFailed');

		let errorMessages = 'Some Changes Failed to Save';
		if (response.errors && response.errors.length > 0)
		{
			errorMessages = response.errors.join('<br/>');
		}

		functions.openModal({
			title: 'Failed',
			html: `<div style="white-space: pre-line; text-align: left;">${errorMessages}</div>`,
		});
	}
}

function resetAll() {
	store.disbursements = store.disbursements.filter(d => !d.details.isNew);
	for (const r of rowRefs.value) {
		r?.resetChanges?.();
	}
	store.clearAllDirty();
	emit("hasDirtyChanged", { hasDirty: false });
}

async function collectChangesForConfirmation(): Promise<DisbursementChangesSummary[]> {
	const summary: DisbursementChangesSummary[] = [];	
	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (!row) continue;
		
		const disbursementId = row.getDisbursementId?.();
		const sequenceNumber = row.getSequenceNumber?.();
		if (disbursement.details.isNew) {
			const formData = row.getFormData?.();
			if (formData) {				
				summary.push({
					certSeq: props.certification.form.sequenceNumber,
					disbSeq: sequenceNumber || 'NEW',
					disbursementId: 0,
					isNew: true,
					newDisbursement: {
						status: DisbHelpers.getStatusDisplayName(
							formData.disbursementStatusTypeId,
							props.certification.disbursementFormOptions.disbursementStatusTypes
						),
						amount: DisbHelpers.formatCurrency(formData.disbursementAmount),
						date: DisbHelpers.formatDate(formData.disbursementDate),
						method: DisbHelpers.getMethodDisplayName(
							formData.disbursementMethodTypeId,
							props.certification.disbursementFormOptions.disbursementMethodTypes
						),
					},
				});
			}
			continue;
		}

		const patchData = await row.getPatches?.();
		const postDisbChange = await row.getPendingPostDisbursementChange?.();
		
		if (!patchData && !postDisbChange) {
			continue;
		}
		
		const changeSummary: DisbursementChangesSummary = {
			certSeq: props.certification.form.sequenceNumber,
			disbSeq: sequenceNumber || '',
			disbursementId: disbursementId || 0,
		};
		
		if (patchData?.patches && patchData.patches.length > 0) {
			type FieldChange = {
				field: string;
				previous: string;
				new: string;
			};

			const fieldChanges: FieldChange[] = [];			
			const originalDisbursement = store.disbursements.find(d => d.details.id === disbursementId);
			for (const patch of patchData.patches) {
				if ('value' in patch) {
					const fieldName = patch.path.substring(1);
					const previousValue = originalDisbursement?.details[fieldName];					
					fieldChanges.push({
						field: DisbHelpers.formatFieldName(fieldName),
						previous: DisbHelpers.formatValue(fieldName, previousValue, props.certification.disbursementFormOptions),
						new: DisbHelpers.formatValue(fieldName, patch.value, props.certification.disbursementFormOptions),
					});
				}
			}
			
			if (fieldChanges.length > 0) {
				changeSummary.fieldChanges = fieldChanges;
			}
		}		
		if (postDisbChange) {
			const originalDisbursement = store.disbursements.find(d => d.details.id === disbursementId);
			if (postDisbChange.patches) {
				const postDisbChanges = postDisbChange.patches.patchesVM.map(patchVM => ({
					field: patchVM.label || patchVM.property,
					previous: String(patchVM.from ?? ''),
					new: String(patchVM.to ?? '')
				}));				
				changeSummary.postDisbChange = {
					isEdit: true,
					changes: postDisbChanges,
				};				
			} else if (postDisbChange.data) {								
				changeSummary.postDisbChange = {
					isEdit: false,
					newStatus: DisbHelpers.getStatusDisplayName(
						originalDisbursement?.details.disbursementStatusTypeId,
						props.certification.disbursementFormOptions.disbursementStatusTypes
					),
					newAmount: DisbHelpers.formatCurrency(postDisbChange.data.disbursementAmount),
					processingDate: DisbHelpers.formatDate(postDisbChange.data.processingDate),
					effectiveDate: DisbHelpers.formatDate(postDisbChange.data.effectiveDate),
					settleDate: DisbHelpers.formatDate(postDisbChange.data.settleDate),
					method: DisbHelpers.getMethodDisplayName(
						postDisbChange.data.disbursementMethodTypeId,
						props.certification.disbursementFormOptions.disbursementMethodTypes
					),
					reason: DisbHelpers.getReasonDisplayName(
						postDisbChange.data.postDisbursementChangeReasonTypeId,
						props.certification.disbursementFormOptions.postDisbursementChangeReasonTypes
					),
				};
			}
		}		
		if (changeSummary.fieldChanges || changeSummary.postDisbChange) {
			summary.push(changeSummary);
		}
	}	
	return summary;
}

function handleAddNew() {
	if (hasReachedLimit.value) {
		functions.openModal({
			title: 'Disbursement Limit Reached',
			description: "You've reached the limit of 4 disbursements per certification.",
		});
		return;
	}

	if (hasDirty.value) {
		functions.openModal({
			title: 'Unsaved Changes',
			description: 'Please save or cancel your current changes before adding a new disbursement.',
		});
		return;
	}

	const newSequenceNumber = "New";
	const defaultMethodTypeId = props.certification.disbursementFormOptions.preferredSchoolDisbursementTypeId;
	const defaultMethodTypeName = props.certification.disbursementFormOptions.preferredSchoolDisbursementTypeName;

	const newDisb: DisbursementVM = {
		details: {
			id: -(store.disbursements.length + 1),
			certificationId: props.certification.id,
			sequenceNumber: newSequenceNumber,
			disbursementStatusTypeId: 5, // Scheduled Disbursement
			disbursementMethodTypeId: defaultMethodTypeId,
			certificationDisbursementStatusReasonTypeId: undefined,
			disbursementAmount: 0,
			disbursementDate: '',
			disbursementStatusTypeName: 'Scheduled Disbursement',
			disbursementMethodTypeName: defaultMethodTypeName,
			certificationDisbursementStatusReasonTypeName: null,
			disbursementAmountDisplay: '',
			disbursementDateDisplay: '',
			isEditing: true,
			isDisbursed: false,
			allowPostDisbursementChange: false,
			hasPendingEdits: false,
			hasPendingPostDisbursementChange: false,
			maxAmountForPostDisbursementChange: 0,
			latestPostDisbursementChange: null,
			isNew: true
		},
		form: {
			id: -(store.disbursements.length + 1),
			certificationId: props.certification.id,
			sequenceNumber: newSequenceNumber,
			disbursementStatusTypeId: 5,
			disbursementMethodTypeId: defaultMethodTypeId,
			certificationDisbursementStatusReasonTypeId: null,
			disbursementAmount: null,
			disbursementDate: null,
			disbursementStatusTypeName: 'Scheduled Disbursement',
			disbursementMethodTypeName: defaultMethodTypeName,
			certificationDisbursementStatusReasonTypeName: null,
			disbursementAmountDisplay: null,
			disbursementDateDisplay: null,
			isEditing: true,
			isDisbursed: false,
			allowPostDisbursementChange: false,
			hasPendingEdits: false,
			hasPendingPostDisbursementChange: false,
			maxAmountForPostDisbursementChange: 0,
			latestPostDisbursementChange: null,
			isNew: true
		},
		formOptions: props.certification.disbursementFormOptions,
	};
	store.disbursements.push(newDisb);
}

watch(() => props.editingPostDisbFromHistory, async (editData) => {
	if (!editData) return;
	
	store.setEditRequest(editData);
	const rowIndex = store.disbursements.findIndex(
		d => d.details.id === editData.disbursementId
	);
	
	if (rowIndex !== -1) {			
		const disbursement = store.disbursements[rowIndex];	
		if (disbursement.details.latestPostDisbursementChange &&
				disbursement.details.latestPostDisbursementChange.disbursementAmountLedgerId === editData.postDisbursementChangeAmountLedgerId) {
			if (disbursement.details.disbursementAmount === null || 
					disbursement.details.disbursementAmount === undefined) {					
				functions.openModal({
					title: 'Error',
					description: 'Cannot edit post-disbursement change: disbursement amount is missing.',
				});
				emit('clearEditRequest');
				store.setEditRequest(null);
				return;
			}
			
			const postDisbData: CreatePostDisbursementChangeRequest = {
				disbursementAmount: disbursement.details.disbursementAmount,
				disbursementAmountDisplay: disbursement.details.disbursementAmountDisplay || '',
				postDisbursementChangeReasonTypeId: disbursement.details.latestPostDisbursementChange.postDisbursementChangeReasonTypeId,
				settleDate: disbursement.details.latestPostDisbursementChange.settleDate,
				effectiveDate: disbursement.details.latestPostDisbursementChange.effectiveDate,
				processingDate: disbursement.details.latestPostDisbursementChange.processingDate,
				disbursementMethodTypeId: disbursement.details.latestPostDisbursementChange.disbursementMethodTypeId,
			};
			
			await nextTick();
			const row = rowRefs.value[rowIndex];
			
			if (row?.expandAndOpenPostDisbursementForm) {					
				row.expandAndOpenPostDisbursementForm(
					postDisbData,
					editData.postDisbursementChangeAmountLedgerId
				);

				nextTick(() => {
					const rowElement = document.querySelector(`[data-disbursement-id="${editData.disbursementId}"]`);
					if (rowElement) {
						rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				});
			}
		} else {				
			functions.openModal({
				title: 'Error',
				description: 'Could not find the post-disbursement change data. The page may need to be refreshed.',
			});
		}
	} else {			
		functions.openModal({
			title: 'Error',
			description: 'Could not find the disbursement row.',
		});
	}
	emit('clearEditRequest');
	store.setEditRequest(null);
	
}, { immediate: true });

defineExpose({ saveAll, resetAll });

const tableHeaders = ref([
	{
		label: "Seq.",
		name: "sequenceNumber",
		title: null,
		key: "sequenceNumber",
		required: false,
		minWidth: "fit-content",
		width: "103px",
	},
	{
		label: "Disb. Status",
		name: "disbursementStatus",
		title: "Disbursement Status",
		key: "disbursementStatusTypeName",
		required: false,
		minWidth: "219px",
		width: "219px",
	},
	{
		label: "Disb. Amount",
		name: "disbursementAmount",
		title: "Disbursement Amount",
		key: "disbursementAmountDisplay",
		required: true,
		width: "90px",
		minWidth: "90px",
	},
	{
		label: "Disb. Date",
		name: "disbursementDate",
		title: "Disbursement Date",
		key: "disbursementDateDisplay",
		required: true,
		width: "90px",
		minWidth: "90px",
	},
	{
		label: "Disb. Method",
		name: "disbursementMethod",
		title: "Disbursement Method",
		key: "disbursementMethodTypeName",
		required: false,
		width: "112px",
		minWidth: "112px",
	},
	{
		label: "",
		name: "",
		title: null,
		key: "",
		required: false,
	},
]);
</script>

<style scoped>
table {
	table-layout: auto;
	height: 100%;
	width: 100%;
	min-width: fit-content;  
}

table thead th {
	color: #000;
	font-family: "Montserrat";
	font-size: 14px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	padding: 0 10px 8px 0px;
	text-align: left;
	white-space: nowrap;
}

table tr {
	width: 100%;
}

.disbursements-table {
	width: 100%;
	table-layout: auto;
	border-collapse: separate;
	border-spacing: 0 5px;
	background-color: transparent;
}

.disbursements-table th,
.disbursements-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.disbursements-table td {
	background-color: #fff;
}

th .required {
	color: #ff0000;
}
table tbody :is(tr:first-of-type) {
	padding-top: 0;
}

th:first-child,
td:first-child {
	padding-left: 1rem !important;
}

table td {
	padding: 0px 10px;
}

.table-header-cell {
	display: table-cell;
	padding: 10px;
	text-align: justify;
	border-bottom: 1px solid black;
}
.resp-table-row {
	display: table-row;
}
.table-body-cell {
	display: table-cell;
}
#resp-table-body {
	display: table-row-group;
}

.add-new-container {
	margin-top: 12px;
	padding: 8px 0;
	display: flex;
	align-items: center;
	gap: 12px;
}

.add-new-link {
	background: none;
	border: none;	
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	padding: 8px 12px;
	text-decoration: none;
	display: flex;
	align-items: center;
	gap: 4px;	
}

.add-new-link:hover:not(:disabled) {	
	text-decoration: underline;
}

.add-new-link:disabled {
	color: #999;
	cursor: not-allowed;
}

.limit-message {
	color: #666;
	font-size: 13px;
	font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 48px 24px;  
}
</style>